import React, { useEffect, useState } from "react";
import axios from "axios";
import { 
  Users, 
  UserCheck, 
  UserX, 
  TrendingUp, 
  Megaphone, 
  BarChart3,
  ArrowUpRight,
  Sun,
  Zap,
  Battery
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, trend, color, onClick }) => (
  <Card 
    className="card-hover cursor-pointer border-slate-100 hover:border-slate-200" 
    onClick={onClick}
    data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, '-')}`}
  >
    <CardContent className="p-3 md:p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs md:text-sm font-medium text-slate-500">{title}</p>
          <p className="text-xl md:text-3xl font-['Outfit'] font-bold mt-1 md:mt-2 text-slate-900">{value}</p>
          {trend && (
            <div className="hidden md:flex items-center gap-1 mt-2 text-sm text-[#1A4D2E]">
              <ArrowUpRight className="w-4 h-4" />
              <span>{trend}</span>
            </div>
          )}
        </div>
        <div className={`p-2 md:p-3 rounded-lg md:rounded-xl ${color}`}>
          <Icon className="w-4 h-4 md:w-6 md:h-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const RecentLeadRow = ({ lead }) => {
  const statusStyles = {
    qualified: "bg-[#1A4D2E]/10 text-[#1A4D2E]",
    disqualified: "bg-[#A94442]/10 text-[#A94442]",
    new: "bg-[#FDB813]/10 text-amber-700",
    contacted: "bg-blue-100 text-blue-700",
    converted: "bg-purple-100 text-purple-700"
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
          <span className="text-sm font-medium text-slate-600">
            {lead.first_name[0]}{lead.last_name[0]}
          </span>
        </div>
        <div>
          <p className="font-medium text-slate-900">{lead.first_name} {lead.last_name}</p>
          <p className="text-sm text-slate-500">{lead.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right">
          <p className="text-sm font-medium text-slate-900">Score: {lead.score}</p>
          <p className="text-xs text-slate-500">{lead.city}, {lead.state}</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${statusStyles[lead.status] || statusStyles.new}`}>
          {lead.status}
        </span>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_leads: 0,
    qualified_leads: 0,
    disqualified_leads: 0,
    conversion_rate: 0,
    ads_generated: 0,
    avg_lead_score: 0
  });
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/leads`)
      ]);
      setStats(statsRes.data);
      setRecentLeads(leadsRes.data.slice(0, 5));
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-['Outfit'] font-bold text-slate-900">Dashboard</h1>
        <p className="text-sm md:text-base text-slate-500 mt-1">Welcome back! Here's your lead generation overview.</p>
      </div>

      {/* Stats Grid - Bento Layout */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
        <StatCard
          title="Total Leads"
          value={stats.total_leads}
          icon={Users}
          color="bg-slate-700"
          onClick={() => navigate("/leads")}
        />
        <StatCard
          title="Qualified Leads"
          value={stats.qualified_leads}
          icon={UserCheck}
          trend="Ready for contact"
          color="bg-[#1A4D2E]"
          onClick={() => navigate("/leads?filter=qualified")}
        />
        <StatCard
          title="Disqualified"
          value={stats.disqualified_leads}
          icon={UserX}
          color="bg-[#A94442]"
          onClick={() => navigate("/leads?filter=disqualified")}
        />
        <StatCard
          title="Ads Generated"
          value={stats.ads_generated}
          icon={Megaphone}
          color="bg-[#FDB813]"
          onClick={() => navigate("/generator")}
        />
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
        {/* Conversion Rate Card */}
        <Card className="border-slate-100" data-testid="conversion-rate-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-medium text-slate-500">Conversion Rate</p>
                <p className="text-4xl font-['Outfit'] font-bold text-[#1A4D2E] mt-1">
                  {stats.conversion_rate}%
                </p>
              </div>
              <TrendingUp className="w-12 h-12 text-[#1A4D2E]/20" />
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2">
              <div 
                className="bg-[#1A4D2E] h-2 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(stats.conversion_rate, 100)}%` }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Average Score Card */}
        <Card className="border-slate-100" data-testid="avg-score-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-medium text-slate-500">Avg Lead Score</p>
                <p className="text-4xl font-['Outfit'] font-bold text-slate-900 mt-1">
                  {stats.avg_lead_score}
                </p>
              </div>
              <BarChart3 className="w-12 h-12 text-slate-200" />
            </div>
            <p className="text-sm text-slate-500">Out of 100 points maximum</p>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-slate-100 bg-gradient-to-br from-[#A94442] to-[#8B3836]" data-testid="quick-actions-card">
          <CardContent className="p-6 text-white">
            <div className="flex items-center gap-2 mb-4">
              <Sun className="w-6 h-6" />
              <h3 className="font-['Outfit'] font-semibold text-lg">Quick Actions</h3>
            </div>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate("/generator")}
                className="w-full bg-white/20 hover:bg-white/30 text-white border-0"
                data-testid="quick-generate-ad-btn"
              >
                <Megaphone className="w-4 h-4 mr-2" />
                Generate New Ad
              </Button>
              <Button 
                onClick={() => window.open("/capture", "_blank")}
                className="w-full bg-white text-[#A94442] hover:bg-white/90 border-0"
                data-testid="quick-lead-form-btn"
              >
                <Zap className="w-4 h-4 mr-2" />
                Open Lead Form
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Leads */}
      <Card className="border-slate-100" data-testid="recent-leads-card">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="font-['Outfit'] text-xl">Recent Leads</CardTitle>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate("/leads")}
            data-testid="view-all-leads-btn"
          >
            View All
          </Button>
        </CardHeader>
        <CardContent>
          {recentLeads.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {recentLeads.map((lead, index) => (
                <RecentLeadRow key={lead.id} lead={lead} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No leads yet. Share your lead capture form to get started!</p>
              <Button 
                className="mt-4 btn-primary"
                onClick={() => window.open("/capture", "_blank")}
                data-testid="get-started-btn"
              >
                Open Lead Capture Form
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
